import { formatAttractionPrice } from "./formatAttractionPrice";

/**
 * סיכום קומפקטי ל-sidebar / סיכום הזמנה — שכבת אטרקציות בלבד.
 *
 * @param {{
 *   includedAttractions?: unknown[],
 *   giftAttractions?: unknown[],
 *   selectedOptionalAttractions?: unknown[],
 *   optionalRows?: Array<{
 *     item?: unknown,
 *     selectedAdults?: number,
 *     selectedChildren?: number,
 *     subtotalIls?: number,
 *     lineLocked?: boolean,
 *   }>,
 *   pricing?: { totalAttractionsBillable?: number, displayAttractionsBillableIls?: number, currency?: string | null },
 *   displayAttractionsBillableIls?: number,
 *   currency?: string | null,
 * }} vm
 */
export function buildAttractionsSummaryViewModel(vm) {
  if (!vm || typeof vm !== "object") {
    return { lines: [], total: 0, currency: "ILS", optionalSubtotal: 0 };
  }

  const currency = vm.pricing?.currency != null ? String(vm.pricing.currency) : "ILS";
  /** @type {{ id: string, name: string, priceText: string, statusLabel: string, amount: number|null, detailLine?: string }[]} */
  const lines = [];

  const inc = Array.isArray(vm.includedAttractions) ? vm.includedAttractions : [];
  for (const item of inc) {
    const id = item?.id != null ? String(item.id) : `inc-${lines.length}`;
    const pkgLabel = String(item?.packageRowStatusLabel || "").trim();
    lines.push({
      id: `sum-inc-${id}`,
      name: String(item?.name || "").trim() || "אטרקציה",
      priceText: "",
      statusLabel: pkgLabel === "כלול" ? "כלול במחיר" : pkgLabel || "כלול במחיר",
      amount: null,
    });
  }

  const gifts = Array.isArray(vm.giftAttractions) ? vm.giftAttractions : [];
  for (const item of gifts) {
    const id = item?.id != null ? String(item.id) : `gift-${lines.length}`;
    const pkgLabel = String(item?.packageRowStatusLabel || "").trim();
    lines.push({
      id: `sum-gift-${id}`,
      name: String(item?.name || "").trim() || "הטבה",
      priceText: "—",
      statusLabel: pkgLabel || "הטבה",
      amount: null,
    });
  }

  const optionalRows = Array.isArray(vm.optionalRows) ? vm.optionalRows : [];
  for (const row of optionalRows) {
    const adults = Math.max(0, Math.floor(Number(row?.selectedAdults) || 0));
    const children = Math.max(0, Math.floor(Number(row?.selectedChildren) || 0));
    if (adults === 0 && children === 0) continue;
    const item = row?.item || {};
    const id = item?.id != null ? String(item.id) : `opt-${lines.length}`;
    const name = String(item?.name || "").trim() || "אטרקציה";
    const sub = Number(row?.subtotalIls);
    const amount = Number.isFinite(sub) ? sub : null;
    const parts = [];
    if (adults > 0) parts.push(`${adults} מבוגר`);
    if (children > 0) parts.push(`${children} ילד`);
    const detailLine = parts.length ? parts.join(" · ") : "";
    const priceText =
      amount != null ? `${Math.round(amount).toLocaleString()} ₪` : "—";
    lines.push({
      id: `sum-opt-${id}`,
      name,
      priceText,
      statusLabel: row?.lineLocked ? "בהזמנה" : "בתשלום",
      amount,
      detailLine,
    });
  }

  const selLegacy = Array.isArray(vm.selectedOptionalAttractions)
    ? vm.selectedOptionalAttractions
    : [];
  if (!optionalRows.length && selLegacy.length) {
    for (const item of selLegacy) {
      const id = item?.id != null ? String(item.id) : `opt-${lines.length}`;
      const pt = formatAttractionPrice(item, { currency });
      const val = item?.price?.value;
      const amount =
        val != null && Number.isFinite(Number(val)) ? Number(val) : null;
      lines.push({
        id: `sum-opt-${id}`,
        name: String(item?.name || "").trim() || "אטרקציה",
        priceText: pt || (amount != null ? `${Math.round(amount).toLocaleString()} ₪` : "—"),
        statusLabel: "בתשלום",
        amount,
      });
    }
  }

  const totalRaw =
    vm.pricing?.totalAttractionsBillable != null
      ? Number(vm.pricing.totalAttractionsBillable)
      : vm.pricing?.displayAttractionsBillableIls != null
        ? Number(vm.pricing.displayAttractionsBillableIls)
        : 0;
  const total = Number.isFinite(totalRaw) ? totalRaw : 0;

  const optionalSubtotal = Number.isFinite(Number(vm.pricing?.optionalSelectedTotal))
    ? Number(vm.pricing.optionalSelectedTotal)
    : 0;

  return {
    lines,
    total: Number.isFinite(total) ? total : 0,
    currency,
    optionalSubtotal,
  };
}
